import PropTypes from "prop-types";
import { useMemo, useState, useEffect, useRef, useCallback } from "react";
import {
  useDebounce,
  useAutoScroll,
  useRecentShops,
  useSearch,
} from "../../../hooks";
import useCategories from "../../../hooks/useCategories";
import { INCLUDES } from "../../../hooks/useSearch";
import { useEmailModal } from "../../../services/EmailModal";
import { CloseIcon, IconButton, SearchIcon } from "../../../themeV2";
import { countResults } from "./kindMeta";
import buildActiveSections from "./buildActiveSections";
import { SearchResultsView, DefaultView } from "./Views";
import { LoadingState, EmptyState } from "./States";
import css from "./SearchV2.module.css";

export default function SearchNavigation({ className }) {
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [q, setQ] = useState("");
  const inputRef = useRef(null);
  const mobileInputRef = useRef(null);
  const rootRef = useRef(null);
  const { scroll } = useAutoScroll();

  useDebounce(
    () => {
      setQ(query.toLowerCase().trim());
    },
    300,
    [query],
  );

  const { results, isLoading } = useSearch(
    q,
    [
      INCLUDES.INFLUENCERS,
      INCLUDES.PRODUCTS,
      INCLUDES.CHARACTERS,
      INCLUDES.FRANCHISES,
    ],
    "search-nav",
  );
  const { recentShops } = useRecentShops();
  const { categories } = useCategories();
  const emailModal = useEmailModal();

  const totalCount = countResults(results);
  const hasResults = totalCount > 0;

  const close = useCallback(() => {
    setIsOpen(false);
    setQuery("");
  }, []);

  const open = useCallback(() => {
    if (!isOpen) {
      setIsOpen(true);
      setTimeout(() => mobileInputRef.current?.focus(), 80);
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return undefined;
    emailModal.block();
    return emailModal.unblock;
  }, [isOpen, emailModal]);

  const handleTriggerClick = useCallback(() => {
    // On mobile the inline input is hidden, so onFocus won't fire.
    // Open the dropdown on click instead.
    open();
  }, [open]);

  const clearQuery = useCallback(() => {
    setQuery("");
    setQ("");
    inputRef.current?.focus();
  }, []);

  // Click-outside to close
  useEffect(() => {
    if (!isOpen) return;

    const handlePointerDown = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) {
        inputRef.current?.blur();
        close();
      }
    };
    document.addEventListener("pointerdown", handlePointerDown);
    return () => document.removeEventListener("pointerdown", handlePointerDown);
  }, [isOpen, close]);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKey = (e) => {
      if (e.key === "k" && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        if (isOpen) {
          inputRef.current?.blur();
          close();
        } else {
          inputRef.current?.focus();
        }
        return;
      }
      if (e.key === "Escape" && isOpen) {
        inputRef.current?.blur();
        close();
      }
    };
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [isOpen, close]);

  const activeSections = useMemo(() => buildActiveSections(results), [results]);

  const onResultClick = useCallback(
    (noScroll = false) => {
      if (!noScroll) {
        scroll();
      }
      inputRef.current?.blur();
      requestAnimationFrame(close);
    },
    [scroll, close],
  );

  return (
    <div ref={rootRef} className={css.root}>
      {/* Inline search trigger / input pill */}
      <div
        className={[css.trigger, className].filter(Boolean).join(" ")}
        onClick={handleTriggerClick}
      >
        <SearchIcon className={css.searchIcon} />
        <input
          ref={inputRef}
          className={css.input}
          type="text"
          placeholder="Search everything…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onFocus={open}
          aria-label="Search"
          aria-expanded={isOpen}
          aria-haspopup="listbox"
        />
        {query.length > 0 && (
          <IconButton
            label="Clear search"
            size="sm"
            className={css.clearBtn}
            onClick={clearQuery}
          >
            <CloseIcon />
          </IconButton>
        )}
      </div>

      {/* Dropdown panel */}
      {isOpen && (
        <div className={css.dropdown} role="listbox">
          {/* Mobile-only input row (inline input is hidden at ≤960px) */}
          <div className={css.mobileInputRow}>
            <SearchIcon className={css.searchIconMuted} />
            <input
              ref={mobileInputRef}
              className={css.mobileInput}
              type="text"
              placeholder="Search creators, products, characters..."
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              aria-label="Search"
            />
            {query.length > 0 && (
              <IconButton
                label="Clear search"
                size="sm"
                className={css.clearBtn}
                onClick={clearQuery}
              >
                <CloseIcon />
              </IconButton>
            )}
          </div>

          {/* Results body */}
          <div className={css.scrollBody}>
            {q.length === 0 ? (
              <DefaultView
                recentShops={recentShops}
                categories={categories}
                onClick={onResultClick}
              />
            ) : hasResults ? (
              <SearchResultsView
                totalCount={totalCount}
                activeSections={activeSections}
                onClick={onResultClick}
                query={q}
              />
            ) : isLoading ? (
              <LoadingState />
            ) : (
              <EmptyState query={query} />
            )}
          </div>
        </div>
      )}
    </div>
  );
}

SearchNavigation.propTypes = {
  className: PropTypes.string,
};
